import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Brain, 
    TrendingUp, 
    Target, 
    ShieldCheck, 
    Scale, 
    CheckCircle2,
    AlertTriangle,
    RotateCcw,
    Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Pre-Trade Checklist Data Structure
const preTradeChecklist = {
    kondisi_diri: {
        title: 'Kondisi Diri',
        title_en: 'Self Condition',
        icon: Brain,
        color: 'blue',
        items: [
            { id: 'kd_1', text: 'Fokus dan emosi stabil', text_en: 'Focused and emotionally stable' },
            { id: 'kd_2', text: 'Tidak FOMO atau terburu-buru', text_en: 'Not FOMO or rushing' },
            { id: 'kd_3', text: 'Siap ikut plan, bukan perasaan', text_en: 'Ready to follow plan, not feelings' }
        ]
    },
    market_context: {
        title: 'Market Context',
        title_en: 'Market Context',
        icon: TrendingUp,
        color: 'emerald',
        items: [
            { id: 'mc_1', text: 'Sesi market jelas (Asia / London / New York)', text_en: 'Market session clear (Asia / London / New York)' },
            { id: 'mc_2', text: 'Arah market difahami (trend / ranging)', text_en: 'Market direction understood (trend / ranging)' },
            { id: 'mc_3', text: 'Volatility sesuai untuk trade', text_en: 'Volatility suitable for trading' }
        ]
    },
    zone_setup: {
        title: 'Zone & Setup',
        title_en: 'Zone & Setup',
        icon: Target,
        color: 'purple',
        items: [
            { id: 'zs_1', text: 'Zone penting sudah ditanda', text_en: 'Important zones are marked' },
            { id: 'zs_2', text: 'Entry hanya di area yang dirancang', text_en: 'Entry only at planned areas' },
            { id: 'zs_3', text: 'Ada confirmation yang jelas', text_en: 'Clear confirmation present' }
        ]
    },
    risk_management: {
        title: 'Risk Management',
        title_en: 'Risk Management',
        icon: ShieldCheck,
        color: 'amber',
        items: [
            { id: 'rm_1', text: 'Risk per trade ikut plan', text_en: 'Risk per trade follows plan' },
            { id: 'rm_2', text: 'Stop Loss ditentukan sebelum entry', text_en: 'Stop Loss determined before entry' },
            { id: 'rm_3', text: 'Target profit munasabah', text_en: 'Profit target is reasonable' }
        ]
    },
    disiplin: {
        title: 'Disiplin',
        title_en: 'Discipline',
        icon: Scale,
        color: 'rose',
        items: [
            { id: 'dp_1', text: 'Tidak kejar market', text_en: 'Not chasing the market' },
            { id: 'dp_2', text: 'Tidak tambah lot ikut emosi', text_en: 'Not adding lots based on emotions' },
            { id: 'dp_3', text: 'Sedia terima loss jika SL kena', text_en: 'Ready to accept loss if SL hits' }
        ]
    },
    final_check: {
        title: 'Final Check',
        title_en: 'Final Check',
        icon: CheckCircle2,
        color: 'primary',
        items: [
            { id: 'fc_1', text: 'Setup jelas & boleh dijelaskan', text_en: 'Setup clear & can be explained' },
            { id: 'fc_2', text: 'Entry ikut rule', text_en: 'Entry follows rules' },
            { id: 'fc_3', text: 'Jika ragu → NO TRADE', text_en: 'If in doubt → NO TRADE' }
        ]
    }
};

const colorStyles = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500/20' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500/20' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', ring: 'ring-purple-500/20' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500/20' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', ring: 'ring-rose-500/20' },
    primary: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', ring: 'ring-red-500/20' }
};

export const PreTradeChecklist = ({ onComplete, className }) => {
    const { language } = useLanguage();
    
    // Initialize checkedItems from localStorage using lazy initialization
    const [checkedItems, setCheckedItems] = useState(() => {
        const saved = localStorage.getItem('preTradeChecklist');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    return parsed.items;
                }
            } catch (e) {
                console.error('Error loading checklist:', e);
            }
        }
        return {};
    });
    const [showReminder, setShowReminder] = useState(false);

    // Calculate total items and checked count
    const totalItems = Object.values(preTradeChecklist).reduce((acc, section) => acc + section.items.length, 0);
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const isComplete = checkedCount === totalItems;
    const progress = Math.round((checkedCount / totalItems) * 100);

    // Ref to track if component has mounted
    const hasMounted = useRef(false);

    // Save to localStorage on change (skip initial mount)
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        
        localStorage.setItem('preTradeChecklist', JSON.stringify({
            date: new Date().toDateString(),
            items: checkedItems
        }));
    }, [checkedItems]);

    const handleToggle = (itemId) => {
        setCheckedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
        setShowReminder(false);
    };

    const handleReset = () => {
        setCheckedItems({});
        setShowReminder(false);
    };

    const handleProceed = () => {
        if (!isComplete) {
            setShowReminder(true);
            return;
        }
        if (onComplete) {
            onComplete();
        }
    };

    // Calculate section completion
    const getSectionProgress = (sectionKey) => {
        const section = preTradeChecklist[sectionKey];
        const sectionChecked = section.items.filter(item => checkedItems[item.id]).length;
        return {
            checked: sectionChecked,
            total: section.items.length,
            complete: sectionChecked === section.items.length
        };
    };

    return (
        <div className={cn("w-full max-w-3xl mx-auto", className)} data-testid="pre-trade-checklist">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                    Pre-Trade Checklist
                </h2>
                <p className="text-slate-500 mt-2">
                    {language === 'id' 
                        ? 'Lengkapkan semua item sebelum memulakan trading session'
                        : 'Complete all items before starting your trading session'
                    }
                </p>
                
                {/* Progress Bar */}
                <div className="mt-6 flex items-center justify-center gap-4">
                    <div className="flex-1 max-w-xs h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                            className={cn(
                                "h-full rounded-full transition-all duration-500 ease-out",
                                isComplete ? "bg-emerald-500" : "bg-primary"
                            )}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className={cn(
                        "text-sm font-semibold",
                        isComplete ? "text-emerald-600" : "text-slate-600"
                    )}>
                        {checkedCount}/{totalItems}
                    </span>
                </div>
            </div>

            {/* Checklist Sections */}
            <div className="space-y-4">
                {Object.entries(preTradeChecklist).map(([sectionKey, section]) => {
                    const Icon = section.icon;
                    const colors = colorStyles[section.color];
                    const sectionProgress = getSectionProgress(sectionKey);

                    return (
                        <div 
                            key={sectionKey}
                            className={cn(
                                "bg-white rounded-2xl border overflow-hidden transition-all duration-300",
                                sectionProgress.complete 
                                    ? "border-emerald-200 shadow-sm shadow-emerald-100" 
                                    : "border-slate-100"
                            )}
                            data-testid={`section-${sectionKey}`}
                        >
                            {/* Section Header */}
                            <div className={cn(
                                "flex items-center gap-3 px-5 py-4 border-b transition-colors",
                                sectionProgress.complete 
                                    ? "bg-emerald-50/50 border-emerald-100" 
                                    : "bg-slate-50/50 border-slate-100"
                            )}>
                                <div className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                                    sectionProgress.complete
                                        ? "bg-emerald-500 text-white"
                                        : `${colors.bg} ${colors.text}`
                                )}>
                                    {sectionProgress.complete 
                                        ? <CheckCircle2 className="w-5 h-5" />
                                        : <Icon className="w-5 h-5" />
                                    }
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900">
                                        {language === 'en' ? section.title_en : section.title}
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        {sectionProgress.checked}/{sectionProgress.total} {language === 'id' ? 'selesai' : 'complete'}
                                    </p>
                                </div>
                                {sectionProgress.complete && (
                                    <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                                        ✓
                                    </span>
                                )}
                            </div>

                            {/* Section Items */}
                            <div className="divide-y divide-slate-50">
                                {section.items.map((item) => {
                                    const isChecked = checkedItems[item.id] || false;

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => handleToggle(item.id)}
                                            className={cn(
                                                "w-full flex items-center gap-4 px-5 py-4 text-left transition-all duration-200 hover:bg-slate-50/50",
                                                isChecked && "bg-emerald-50/30"
                                            )}
                                            data-testid={`item-${item.id}`}
                                        >
                                            {/* Checkbox */}
                                            <div className={cn(
                                                "w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200",
                                                isChecked
                                                    ? "bg-emerald-500 border-emerald-500 text-white"
                                                    : "border-slate-200 hover:border-slate-300"
                                            )}>
                                                {isChecked && (
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Item Text */}
                                            <span className={cn(
                                                "flex-1 text-sm md:text-base transition-colors",
                                                isChecked 
                                                    ? "text-slate-400 line-through" 
                                                    : "text-slate-700"
                                            )}>
                                                {language === 'en' ? item.text_en : item.text}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reminder Message */}
            {showReminder && !isComplete && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="reminder-message"
                >
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-amber-800">
                            {language === 'id' 
                                ? 'Satu atau lebih checklist belum dipenuhi.'
                                : 'One or more checklist items are not complete.'
                            }
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                            {language === 'id'
                                ? 'Jangan paksa entry. Pastikan semua item ditanda sebelum trade.'
                                : "Don't force entry. Make sure all items are checked before trading."
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {isComplete && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="success-message"
                >
                    <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-emerald-800">
                            {language === 'id'
                                ? 'Checklist lengkap!'
                                : 'Checklist complete!'
                            }
                        </p>
                        <p className="text-sm text-emerald-700 mt-1">
                            {language === 'id'
                                ? 'Anda boleh trade dengan disiplin. Good luck!'
                                : 'You can trade with discipline. Good luck!'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                    variant="outline"
                    onClick={handleReset}
                    className="w-full sm:w-auto rounded-xl px-6"
                    data-testid="reset-btn"
                >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Reset Checklist' : 'Reset Checklist'}
                </Button>
                
                <Button
                    onClick={handleProceed}
                    className={cn(
                        "w-full sm:w-auto rounded-xl px-8",
                        isComplete 
                            ? "bg-emerald-600 hover:bg-emerald-700" 
                            : "bg-primary hover:bg-primary/90"
                    )}
                    data-testid="proceed-btn"
                >
                    {isComplete ? (
                        <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Siap Trade' : 'Ready to Trade'}
                        </>
                    ) : (
                        language === 'id' ? 'Semak Checklist' : 'Check Checklist'
                    )}
                </Button>
            </div>
        </div>
    );
};
